import { onDocumentReady } from '@theme/utilities';

(() => {
  "use strict";

  const CONFIG = {
    morphDelay: 50,
    selectors: {
      horizonDialog: 'dialog.quick-add-modal',
      customDialog: 'dialog.custom-variant-quick-add-modal',
      customModalContent: '#custom-variant-quick-add-modal-content',
      variantPicker: 'variant-picker',
      variantInputRadio: 'input[type="radio"]:checked[data-variant-id]',
      variantInputOption: 'select option:checked[data-variant-id]',
      fabricBadge: '[data-badge-id="fabric"]',
      statsBadge: '[data-badge-id="stats"]',
      badgeText: '.custom-badge__text',
      mediaItem: '.product-media[data-media-id]',
    },
    classes: {
      inactive: 'custom-badge--inactive',
    },
  };

  const state = {
    /** @type {string|null} */
    currentVariantId: null,
    /** @type {boolean} */
    isDialogOpen: false,
  };

  /**
   * Gets elements relative to a container
   * @param {Element} container - The container element
   */
  const getElements = (container) => ({
    variantPicker: container.querySelector(CONFIG.selectors.variantPicker),
    fabricBadge: container.querySelector(CONFIG.selectors.fabricBadge),
    mediaContainers: () => container.querySelectorAll(CONFIG.selectors.mediaItem),
  });

  /**
   * Gets selected variant control (radio or option)
   * @param {Element} container - The container element
   * @returns {HTMLElement | HTMLOptionElement | null}
   */
  const getCurrentVariantElement = (container) => {
    const checkedRadio = container.querySelector(CONFIG.selectors.variantInputRadio);
    if (checkedRadio instanceof HTMLElement) return checkedRadio;

    const checkedOption = container.querySelector(CONFIG.selectors.variantInputOption);
    if (checkedOption instanceof HTMLOptionElement) return checkedOption;

    return null;
  };

  /**
   * Gets current variant ID
   * @param {Element} container - The container element
   * @returns {string|null}
   */
  const getCurrentVariantId = (container) => {
    const selectedVariant = getCurrentVariantElement(container);
    return selectedVariant?.dataset.variantId || null;
  };

  /**
   * Gets current fabric weight from Liquid-provided data attribute
   * @param {Element} container - The container element
   * @returns {string|undefined}
   */
  const getCurrentFabricWeight = (container) => {
    const selectedVariant = getCurrentVariantElement(container);
    const value = selectedVariant?.dataset.customFabricWeight;
    return value ? value : undefined;
  };

  /**
   * Updates a badge with a value or hides it
   * @param {Element|null} badge - The badge element
   * @param {string|undefined} value - The value to display
   */
  const updateBadge = (badge, value) => {
    if (!badge) return;

    const textElement = badge.querySelector(CONFIG.selectors.badgeText);
    if (!textElement) return;

    if (value) {
      textElement.textContent = value;
      badge.classList.remove(CONFIG.classes.inactive);
    } else {
      badge.classList.add(CONFIG.classes.inactive);
    }
  };

  /**
   * Updates all badges based on current state
   * @param {ReturnType<typeof getElements>} elements - Element references
   * @param {Element} container - Modal content container
   */
  const updateAllBadges = (elements, container) => {
    if (elements.fabricBadge) {
      updateBadge(elements.fabricBadge, getCurrentFabricWeight(container));
    }

    const mediaContainers = elements.mediaContainers();
    mediaContainers.forEach((mediaContainer) => {
      if (!(mediaContainer instanceof HTMLElement)) return;

      const modelStats = mediaContainer.dataset.customModelStats;
      const statsBadge = mediaContainer.querySelector(CONFIG.selectors.statsBadge);
      updateBadge(statsBadge, modelStats || undefined);
    });
  };

  /**
   * Patches variant picker to update badges after variant change
   * @param {Element|null} variantPicker - The variant picker element
   * @param {Element} container - The container element
   */
  const patchVariantPicker = (variantPicker, container) => {
    if (!variantPicker) return;

    const waitForComponent = () => {
      // @ts-ignore - Custom property on web component
      if (!variantPicker.updateVariantPicker) {
        setTimeout(waitForComponent, 100);
        return;
      }

      // @ts-ignore - Custom property for tracking
      if (!variantPicker._quickAddBadgesOriginalUpdate) {
        // @ts-ignore
        variantPicker._quickAddBadgesOriginalUpdate = variantPicker.updateVariantPicker;
      }

      // @ts-ignore
      variantPicker.updateVariantPicker = function (newHtml) {
        // @ts-ignore
        const result = variantPicker._quickAddBadgesOriginalUpdate.call(this, newHtml);

        setTimeout(() => {
          try {
            const elements = getElements(container);
            state.currentVariantId = getCurrentVariantId(container);
            updateAllBadges(elements, container);
          } catch (error) {
            console.error('[QuickAddBadges] Failed to update after variant change:', error);
          }
        }, CONFIG.morphDelay);

        return result;
      };
    };

    waitForComponent();
  };

  /**
   * Initializes badges for a modal container
   * @param {Element} container - The modal content container
   */
  const initializeBadges = (container) => {
    try {
      const elements = getElements(container);
      if (!elements.variantPicker) return;

      state.currentVariantId = getCurrentVariantId(container);
      updateAllBadges(elements, container);
      patchVariantPicker(elements.variantPicker, container);
    } catch (error) {
      console.error('[QuickAddBadges] Failed to initialize:', error);
    }
  };

  /**
   * Clears state when dialog closes
   */
  const clearState = () => {
    state.currentVariantId = null;
  };

  /**
   * Creates a dialog observer for open/close events
   * @param {Element} dialog - The dialog element
   * @param {Element|null} contentContainer - Optional content container (for custom modal)
   */
  const observeDialog = (dialog, contentContainer = null) => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'open') continue;

        const isNowOpen = dialog.hasAttribute('open');

        if (isNowOpen && !state.isDialogOpen) {
          state.isDialogOpen = true;
          const container = contentContainer || dialog;
          if (container) {
            initializeBadges(container);
          }
        } else if (!isNowOpen && state.isDialogOpen) {
          state.isDialogOpen = false;
          clearState();
        }
      }
    });

    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  };

  onDocumentReady(() => {
    const horizonDialog = document.querySelector(CONFIG.selectors.horizonDialog);
    if (horizonDialog) {
      observeDialog(horizonDialog);
    }

    const customDialog = document.querySelector(CONFIG.selectors.customDialog);
    if (customDialog) {
      const modalContent = customDialog.querySelector(CONFIG.selectors.customModalContent);
      observeDialog(customDialog, modalContent);
    }
  });
})();
